import React from 'react';
import { X, CheckCircle, Bot, Mic2, FileAudio, Wand2, Brain, Globe, MessageSquare, Sparkles } from 'lucide-react';

interface HelpModalProps {
  isOpen: boolean;
  onClose: () => void;
  language: 'nl' | 'en' | 'tr';
}

const translations = {
  nl: {
    title: 'Help & Features',
    features: [
      {
        icon: <Globe className="w-6 h-6" />,
        title: 'URL Analyse',
        description: 'Plak een URL en onze AI analyseert automatisch de inhoud'
      },
      {
        icon: <Brain className="w-6 h-6" />,
        title: 'AI Verwerking',
        description: 'Geavanceerde AI technologie verwerkt de informatie'
      },
      {
        icon: <MessageSquare className="w-6 h-6" />,
        title: 'Script Generatie',
        description: 'De AI creëert een natuurlijk klinkend gesprek'
      },
      {
        icon: <Sparkles className="w-6 h-6" />,
        title: 'Stemoptimalisatie',
        description: 'De stemmen worden verfijnd voor natuurlijke interactie'
      }
    ],
    instructions: [
      'Plak een URL van een artikel of webpagina',
      'Klik op "Genereer Podcast" en wacht ongeveer 2 minuten',
      'Beluister de podcast direct in de browser',
      'Download de audio of het transcript indien gewenst'
    ],
    close: 'Sluiten'
  },
  en: {
    title: 'Help & Features',
    features: [
      {
        icon: <Globe className="w-6 h-6" />,
        title: 'URL Analysis',
        description: 'Paste a URL and our AI automatically analyzes the content'
      },
      {
        icon: <Brain className="w-6 h-6" />,
        title: 'AI Processing',
        description: 'Advanced AI technology processes the information'
      },
      {
        icon: <MessageSquare className="w-6 h-6" />,
        title: 'Script Generation',
        description: 'The AI creates a naturally flowing conversation'
      },
      {
        icon: <Sparkles className="w-6 h-6" />,
        title: 'Voice Optimization',
        description: 'Voices are refined for natural interaction'
      }
    ],
    instructions: [
      'Paste a URL of an article or webpage',
      'Click "Generate Podcast" and wait about 2 minutes',
      'Listen to the podcast directly in the browser',
      'Download the audio or transcript if desired'
    ],
    close: 'Close'
  },
  tr: {
    title: 'Yardım & Özellikler',
    features: [
      {
        icon: <Globe className="w-6 h-6" />,
        title: 'URL Analizi',
        description: 'Bir URL yapıştırın ve yapay zeka içeriği otomatik olarak analiz eder'
      },
      {
        icon: <Brain className="w-6 h-6" />,
        title: 'AI İşleme',
        description: 'Gelişmiş yapay zeka teknolojisi bilgiyi işler'
      },
      {
        icon: <MessageSquare className="w-6 h-6" />,
        title: 'Script Oluşturma',
        description: 'AI doğal bir konuşma akışı oluşturur'
      },
      {
        icon: <Sparkles className="w-6 h-6" />,
        title: 'Ses Optimizasyonu',
        description: 'Sesler doğal etkileşim için iyileştirilir'
      }
    ],
    instructions: [
      'Bir makale veya web sayfası URL\'si yapıştırın',
      '"Podcast Oluştur" düğmesine tıklayın ve yaklaşık 2 dakika bekleyin',
      'Podcast\'i doğrudan tarayıcıda dinleyin',
      'İsterseniz ses dosyasını veya transkripti indirin'
    ],
    close: 'Kapat'
  }
};

export function HelpModal({ isOpen, onClose, language }: HelpModalProps) {
  if (!isOpen) return null;

  const t = translations[language];

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto animate-modal">
        <div className="p-6 border-b border-gray-200 dark:border-gray-800 flex justify-between items-center">
          <h2 className="text-2xl font-bold text-orange-500">{t.title}</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
          >
            <X className="w-6 h-6 text-gray-500" />
          </button>
        </div>
        
        <div className="p-6 space-y-8">
          <section>
            <h3 className="text-xl font-semibold text-orange-500 mb-4">Features</h3>
            <div className="grid gap-4 sm:grid-cols-2">
              {t.features.map((feature, index) => (
                <div
                  key={index}
                  className="p-4 border border-gray-200 dark:border-gray-800 rounded-lg hover:border-orange-500 transition-colors"
                >
                  <div className="flex items-center space-x-3 mb-2">
                    <div className="text-orange-500">{feature.icon}</div>
                    <h4 className="font-medium text-gray-900 dark:text-white">{feature.title}</h4>
                  </div>
                  <p className="text-gray-600 dark:text-gray-300 text-sm">{feature.description}</p>
                </div>
              ))}
            </div>
          </section>

          <section>
            <h3 className="text-xl font-semibold text-orange-500 mb-4">Instructions</h3>
            <div className="space-y-3">
              {t.instructions.map((instruction, index) => (
                <div key={index} className="flex items-start space-x-3">
                  <CheckCircle className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
                  <p className="text-gray-600 dark:text-gray-300">{instruction}</p>
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}